import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Calendar } from '../components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { toast } from 'sonner';
import { format, startOfWeek, endOfWeek, subWeeks } from 'date-fns';
import { 
  CalendarIcon, 
  FileDown, 
  FileBarChart,
  Loader2,
  User,
  Printer
} from 'lucide-react';

const ReportsPage = () => {
  const { API_URL, getAuthHeader, user } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  const today = new Date();
  const [startDate, setStartDate] = useState(startOfWeek(today, { weekStartsOn: 0 }));
  const [endDate, setEndDate] = useState(endOfWeek(today, { weekStartsOn: 0 }));

  const fetchReport = async () => {
    setLoading(true);
    try {
      const start = format(startDate, 'yyyy-MM-dd');
      const end = format(endDate, 'yyyy-MM-dd');
      const response = await axios.get(
        `${API_URL}/api/reports/weekly?start_date=${start}&end_date=${end}`,
        { headers: getAuthHeader() }
      );
      setReport(response.data);
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('حدث خطأ في تحميل التقرير');
    } finally {
      setLoading(false);
    }
  };

  const setThisWeek = () => {
    setStartDate(startOfWeek(today, { weekStartsOn: 0 }));
    setEndDate(endOfWeek(today, { weekStartsOn: 0 }));
  };

  const setLastWeek = () => {
    const lastWeek = subWeeks(today, 1);
    setStartDate(startOfWeek(lastWeek, { weekStartsOn: 0 }));
    setEndDate(endOfWeek(lastWeek, { weekStartsOn: 0 }));
  };

  const exportToCSV = () => {
    if (!report || report.customers.length === 0) return;
    setExporting(true);
    
    try {
      const BOM = '\uFEFF';
      let csvContent = BOM;
      csvContent += `تقرير المبيعات الأسبوعي\n`;
      csvContent += `من: ${report.start_date} إلى: ${report.end_date}\n\n`;
      
      report.customers.forEach(customer => {
        csvContent += `\n=== ${customer.customer_name} ===\n`;
        if (customer.shop_name) csvContent += `المحل: ${customer.shop_name}\n`;
        csvContent += `التاريخ,اليوم,المنتجات,المبلغ\n`;
        
        customer.orders.forEach(order => {
          const items = order.items.map(i => `${i.product_id}:${i.quantity}`).join(' | ');
          csvContent += `${order.date},${order.day_name},"${items}",${order.total_with_tax}\n`;
        });
        
        csvContent += `\nالإجمالي: ${customer.total_with_tax}\n`;
        csvContent += `المدفوع: ${customer.total_paid}\n`;
        csvContent += `المتبقي: ${customer.balance}\n`;
      });
      
      csvContent += `\n\n=== الإجمالي العام ===\n`;
      csvContent += `إجمالي المبيعات: ${report.grand_total}\n`;
      csvContent += `إجمالي المدفوع: ${report.total_paid}\n`;
      csvContent += `إجمالي المتبقي: ${report.total_remaining}\n`;
      csvContent += `المصروفات: ${report.total_expenses}\n`;
      csvContent += `صافي الربح: ${report.net_profit}\n`;
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `تقرير_${report.start_date}_${report.end_date}.csv`;
      link.click();
      
      toast.success('تم تصدير التقرير بنجاح');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('حدث خطأ في تصدير التقرير');
    } finally {
      setExporting(false);
    }
  };

  const handlePrint = () => {
    if (!report) return;
    
    const printHTML = `
      <!DOCTYPE html>
      <html dir="rtl">
      <head>
        <meta charset="UTF-8">
        <title>التقرير الأسبوعي</title>
        <style>
          @page { size: A4; margin: 10mm; }
          body { font-family: Arial, sans-serif; padding: 15px; direction: rtl; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 15px; }
          th, td { padding: 8px; border: 1px solid #ddd; text-align: right; font-size: 11px; }
          th { background: #f5f5f5; }
          h1, h2, h3 { margin: 0 0 10px 0; }
          .customer-section { page-break-inside: avoid; margin-bottom: 20px; border: 1px solid #ddd; padding: 10px; }
          .summary { display: flex; gap: 15px; margin-bottom: 15px; flex-wrap: wrap; }
          .summary-item { padding: 10px; border: 1px solid #ddd; min-width: 100px; text-align: center; }
        </style>
      </head>
      <body>
        <h1 style="text-align: center;">التقرير الأسبوعي</h1>
        <p style="text-align: center; color: #666;">من ${format(startDate, 'yyyy/MM/dd')} إلى ${format(endDate, 'yyyy/MM/dd')}</p>
        
        <div class="summary">
          <div class="summary-item"><strong>إجمالي المبيعات</strong><br/>${formatCurrency(report.grand_total)} €</div>
          <div class="summary-item"><strong>المدفوع</strong><br/>${formatCurrency(report.total_paid)} €</div>
          <div class="summary-item"><strong>المتبقي</strong><br/>${formatCurrency(report.total_remaining)} €</div>
          <div class="summary-item"><strong>صافي الربح</strong><br/>${formatCurrency(report.net_profit)} €</div>
        </div>
        
        ${report.customers.map(customer => `
          <div class="customer-section">
            <h3>${customer.customer_name} ${customer.shop_name ? `- ${customer.shop_name}` : ''}</h3>
            <table>
              <thead>
                <tr>
                  <th>التاريخ</th>
                  <th>المنتجات</th>
                  <th>المبلغ</th>
                </tr>
              </thead>
              <tbody>
                ${customer.orders.map(order => `
                  <tr>
                    <td>${order.date}</td>
                    <td>${order.items ? order.items.map(item => `${item.product_name || item.name}: ${item.quantity}`).join(', ') : '-'}</td>
                    <td style="font-weight: bold;">${formatCurrency(order.total_with_tax)} €</td>
                  </tr>
                `).join('')}
                <tr style="background: #f5f5f5;">
                  <td colspan="2"><strong>المجموع</strong></td>
                  <td><strong>${formatCurrency(customer.total_with_tax)} €</strong></td>
                </tr>
              </tbody>
            </table>
            <div style="display: flex; gap: 20px; margin-top: 10px;">
              <span>الدفعات: ${formatCurrency(customer.total_paid)} €</span>
              <span style="color: ${customer.balance > 0 ? 'red' : 'green'}">المتبقي: ${formatCurrency(customer.balance)} €</span>
            </div>
          </div>
        `).join('')}
        
        <script>window.onload = function() { window.print(); }</script>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (printWindow) {
      printWindow.document.write(printHTML);
      printWindow.document.close();
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount || 0);
  };

  // Helper to get quantity from items array
  const getItemQty = (items, productId) => {
    const item = items?.find(i => i.product_id === productId);
    return item ? item.quantity : 0;
  };

  return (
    <div data-testid="reports-page" className="space-y-6">
      {/* Header */}
      <div className="no-print">
        <h1 className="text-2xl md:text-3xl font-bold text-stone-900">التقارير الأسبوعية</h1>
        <p className="text-stone-500 mt-1">عرض حسابات مفصلة لكل زبون</p>
      </div>

      {/* Date Range Selection */}
      <Card className="border-stone-200 no-print">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <CalendarIcon className="w-5 h-5 text-amber-600" />
            الفترة الزمنية
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3 mb-4">
            <Button variant="outline" data-testid="this-week-btn" onClick={setThisWeek} className="text-sm">
              هذا الأسبوع
            </Button>
            <Button variant="outline" data-testid="last-week-btn" onClick={setLastWeek} className="text-sm">
              الأسبوع الماضي
            </Button>
          </div>
          
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-sm text-stone-600">من:</span>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" data-testid="start-date-btn" className="justify-start text-right font-normal">
                    <CalendarIcon className="ms-2 h-4 w-4" />
                    {format(startDate, 'yyyy/MM/dd')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={startDate} onSelect={(date) => date && setStartDate(date)} initialFocus />
                </PopoverContent>
              </Popover>
            </div>
            
            <div className="flex items-center gap-2">
              <span className="text-sm text-stone-600">إلى:</span>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" data-testid="end-date-btn" className="justify-start text-right font-normal">
                    <CalendarIcon className="ms-2 h-4 w-4" />
                    {format(endDate, 'yyyy/MM/dd')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={endDate} onSelect={(date) => date && setEndDate(date)} initialFocus />
                </PopoverContent>
              </Popover>
            </div>
            
            <Button
              data-testid="generate-report-btn"
              onClick={fetchReport}
              className="bg-amber-600 hover:bg-amber-700 text-white font-bold"
              disabled={loading}
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin me-2" /> : <FileBarChart className="w-4 h-4 me-2" />}
              إنشاء التقرير
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Report Content */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner w-10 h-10"></div>
        </div>
      ) : report ? (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 no-print">
            <Card className="border-emerald-200 bg-emerald-50">
              <CardContent className="p-4">
                <p className="text-sm text-emerald-600">إجمالي المبيعات</p>
                <p className="text-xl font-bold text-emerald-700">{formatCurrency(report.grand_total)} €</p>
              </CardContent>
            </Card>
            <Card className="border-blue-200 bg-blue-50">
              <CardContent className="p-4">
                <p className="text-sm text-blue-600">المدفوع</p>
                <p className="text-xl font-bold text-blue-700">{formatCurrency(report.total_paid)} €</p>
              </CardContent>
            </Card>
            <Card className="border-orange-200 bg-orange-50">
              <CardContent className="p-4">
                <p className="text-sm text-orange-600">المتبقي</p>
                <p className="text-xl font-bold text-orange-700">{formatCurrency(report.total_remaining)} €</p>
              </CardContent>
            </Card>
            <Card className="border-red-200 bg-red-50">
              <CardContent className="p-4">
                <p className="text-sm text-red-600">المصروفات</p>
                <p className="text-xl font-bold text-red-700">{formatCurrency(report.total_expenses)} €</p>
              </CardContent>
            </Card>
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="p-4">
                <p className="text-sm text-amber-600">صافي الربح</p>
                <p className="text-xl font-bold text-amber-700">{formatCurrency(report.net_profit)} €</p>
              </CardContent>
            </Card>
          </div>

          {/* Export Buttons */}
          <div className="flex gap-2 no-print">
            <Button variant="outline" data-testid="export-csv-btn" onClick={exportToCSV} disabled={exporting || report.customers.length === 0}>
              {exporting ? <Loader2 className="w-4 h-4 animate-spin me-2" /> : <FileDown className="w-4 h-4 me-2" />}
              تصدير CSV
            </Button>
            <Button variant="outline" data-testid="print-report-btn" onClick={handlePrint} disabled={report.customers.length === 0}>
              <Printer className="w-4 h-4 me-2" />
              طباعة
            </Button>
          </div>

          {/* Customer Reports */}
          {report.customers.length === 0 ? (
            <div className="empty-state no-print">
              <FileBarChart className="empty-state-icon" />
              <h3 className="empty-state-title">لا يوجد بيانات</h3>
              <p className="empty-state-description">لا يوجد طلبات في هذه الفترة</p>
            </div>
          ) : (
            <div className="space-y-4">
              <h2 className="text-xl font-bold text-stone-900">تفاصيل حساب كل زبون ({report.customers.length} زبون)</h2>
              
              <Accordion type="multiple" className="space-y-3 no-print">
                {report.customers.map((customer) => (
                  <AccordionItem key={customer.customer_id} value={customer.customer_id} className="border border-stone-200 rounded-xl overflow-hidden bg-white">
                    <AccordionTrigger className="px-4 py-3 hover:bg-stone-50">
                      <div className="flex items-center justify-between w-full pe-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                            <User className="w-5 h-5 text-amber-600" />
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-stone-900">{customer.customer_name}</p>
                            {customer.shop_name && <p className="text-sm text-stone-500">{customer.shop_name}</p>}
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-left">
                            <p className="text-sm text-stone-500">الإجمالي</p>
                            <p className="font-bold text-emerald-600">{formatCurrency(customer.total_with_tax)} €</p>
                          </div>
                          {customer.balance > 0 && (
                            <div className="text-left">
                              <p className="text-sm text-stone-500">المتبقي</p>
                              <p className="font-bold text-red-600">{formatCurrency(customer.balance)} €</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pb-4">
                      {/* Orders Table */}
                      <div className="mb-4">
                        <h4 className="font-bold text-stone-700 mb-2">الطلبات ({customer.orders.length})</h4>
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-stone-50">
                              <TableHead className="text-right font-bold">التاريخ</TableHead>
                              <TableHead className="text-right font-bold">اليوم</TableHead>
                              <TableHead className="text-right font-bold">كبير</TableHead>
                              <TableHead className="text-right font-bold">صغير</TableHead>
                              <TableHead className="text-right font-bold">أسمر</TableHead>
                              <TableHead className="text-right font-bold">المبلغ</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {customer.orders.map((order, idx) => (
                              <TableRow key={idx}>
                                <TableCell>{order.date}</TableCell>
                                <TableCell>{order.day_name}</TableCell>
                                <TableCell className="font-bold">{getItemQty(order.items, 'big_box') || '-'}</TableCell>
                                <TableCell className="font-bold">{getItemQty(order.items, 'small_bread') || '-'}</TableCell>
                                <TableCell className="font-bold">{getItemQty(order.items, 'brown_bread') || '-'}</TableCell>
                                <TableCell className="font-bold text-emerald-600">{formatCurrency(order.total_with_tax)} €</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>

                      {/* Products Summary */}
                      {customer.products && customer.products.length > 0 && (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-4 bg-stone-50 rounded-lg mb-4">
                          {customer.products.map((prod, idx) => (
                            <div key={idx}>
                              <p className="text-sm text-stone-500">{prod.product_name}</p>
                              <p className="font-bold">{prod.total_boxes} صندوق</p>
                              <p className="text-sm text-stone-500">({prod.total_breads} ربطة)</p>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Payment Summary */}
                      <div className="p-4 border border-stone-200 rounded-lg">
                        <div className="grid grid-cols-3 gap-4 text-center">
                          <div>
                            <p className="text-sm text-stone-500">الإجمالي</p>
                            <p className="text-xl font-bold text-stone-900">{formatCurrency(customer.total_with_tax)} €</p>
                          </div>
                          <div>
                            <p className="text-sm text-stone-500">المدفوع</p>
                            <p className="text-xl font-bold text-emerald-600">{formatCurrency(customer.total_paid)} €</p>
                          </div>
                          <div>
                            <p className="text-sm text-stone-500">المتبقي</p>
                            <p className={`text-xl font-bold ${customer.balance > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                              {formatCurrency(customer.balance)} €
                            </p>
                          </div>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>

              {/* Print Version - Each customer on a separate page */}
              <div className="hidden print:block" dir="rtl">
                {report.customers.map((customer) => {
                  // Build product name map from the products summary
                  const prodNameMap = {};
                  customer.products?.forEach(p => {
                    if (p.product_id) prodNameMap[p.product_id] = p.product_name;
                  });

                  // Collect all unique product IDs from orders (preserving order)
                  const productIds = [];
                  customer.orders.forEach(order => {
                    order.items?.forEach(item => {
                      if (!productIds.includes(item.product_id)) {
                        productIds.push(item.product_id);
                      }
                    });
                  });

                  return (
                  <div key={customer.customer_id} className="print-customer-page" style={{fontFamily: 'Arial, Tahoma, sans-serif'}}>
                    {/* Bakery Header */}
                    <div style={{textAlign: 'center', borderBottom: '3px solid #d97706', paddingBottom: '15px', marginBottom: '20px'}}>
                      <h1 style={{fontSize: '24px', fontWeight: 'bold', margin: '0 0 5px 0'}}>
                        {user?.company_info?.company_name || 'المخبز'}
                      </h1>
                      {user?.company_info?.phone && (
                        <div style={{fontSize: '13px', color: '#555'}}>هاتف: {user.company_info.phone}</div>
                      )}
                      {user?.company_info?.address && (
                        <div style={{fontSize: '13px', color: '#555'}}>{user.company_info.address}</div>
                      )}
                    </div>

                    {/* Customer Info */}
                    <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '20px', border: '2px solid #d97706', borderRadius: '8px', padding: '12px'}}>
                      <div>
                        <div style={{fontSize: '12px', color: '#888'}}>اسم الزبون</div>
                        <div style={{fontSize: '20px', fontWeight: 'bold'}}>{customer.customer_name}</div>
                        {customer.shop_name && <div style={{fontSize: '13px', color: '#666'}}>{customer.shop_name}</div>}
                      </div>
                      <div style={{textAlign: 'left'}}>
                        <div style={{fontSize: '12px', color: '#888'}}>الفترة</div>
                        <div style={{fontSize: '14px', fontWeight: 'bold'}}>{report.start_date} — {report.end_date}</div>
                      </div>
                    </div>

                    {/* Orders Table */}
                    <table style={{width: '100%', borderCollapse: 'collapse', marginBottom: '20px', fontSize: '13px'}}>
                      <thead>
                        <tr style={{backgroundColor: '#1e40af', color: 'white'}}>
                          <th style={{padding: '8px', border: '1px solid #1e40af', textAlign: 'right'}}>التاريخ</th>
                          <th style={{padding: '8px', border: '1px solid #1e40af', textAlign: 'right'}}>اليوم</th>
                          {productIds.map(pid => (
                            <th key={pid} style={{padding: '8px', border: '1px solid #1e40af', textAlign: 'center'}}>
                              {prodNameMap[pid] || pid}
                            </th>
                          ))}
                          <th style={{padding: '8px', border: '1px solid #1e40af', textAlign: 'center'}}>المبلغ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {customer.orders.map((order, idx) => (
                          <tr key={idx} style={{backgroundColor: idx % 2 === 0 ? '#f8fafc' : '#fff'}}>
                            <td style={{padding: '7px', border: '1px solid #ddd'}}>{order.date}</td>
                            <td style={{padding: '7px', border: '1px solid #ddd'}}>{order.day_name}</td>
                            {productIds.map(pid => (
                              <td key={pid} style={{padding: '7px', border: '1px solid #ddd', textAlign: 'center', fontWeight: 'bold'}}>
                                {getItemQty(order.items, pid) || '-'}
                              </td>
                            ))}
                            <td style={{padding: '7px', border: '1px solid #ddd', textAlign: 'center', fontWeight: 'bold', color: '#1e40af'}}>
                              {formatCurrency(order.total_with_tax)} €
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {/* Products Summary */}
                    {customer.products && customer.products.length > 0 && (
                      <div style={{display: 'flex', gap: '15px', flexWrap: 'wrap', justifyContent: 'center', marginBottom: '20px', padding: '15px', border: '1px solid #fbbf24', borderRadius: '8px', backgroundColor: '#fffbeb'}}>
                        {customer.products.map((prod, idx) => (
                          <div key={idx} style={{textAlign: 'center', padding: '8px 20px', border: '1px solid #fcd34d', borderRadius: '8px', backgroundColor: '#fff', minWidth: '100px'}}>
                            <div style={{fontSize: '22px', fontWeight: 'bold', color: '#b45309'}}>{prod.total_boxes}</div>
                            <div style={{fontSize: '12px', color: '#78350f'}}>{prod.product_name}</div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Financial Summary */}
                    <div style={{display: 'flex', justifyContent: 'space-around', padding: '15px', borderRadius: '8px', backgroundColor: '#1e293b', color: 'white'}}>
                      <div style={{textAlign: 'center'}}>
                        <div style={{fontSize: '12px', opacity: 0.7}}>الإجمالي</div>
                        <div style={{fontSize: '20px', fontWeight: 'bold'}}>{formatCurrency(customer.total_with_tax)} €</div>
                      </div>
                      <div style={{textAlign: 'center'}}>
                        <div style={{fontSize: '12px', opacity: 0.7}}>المدفوع</div>
                        <div style={{fontSize: '20px', fontWeight: 'bold', color: '#4ade80'}}>{formatCurrency(customer.total_paid)} €</div>
                      </div>
                      <div style={{textAlign: 'center', backgroundColor: customer.balance > 0 ? '#ef4444' : '#22c55e', padding: '10px 20px', borderRadius: '8px'}}>
                        <div style={{fontSize: '12px'}}>المتبقي</div>
                        <div style={{fontSize: '22px', fontWeight: 'bold'}}>{formatCurrency(customer.balance)} €</div>
                      </div>
                    </div>

                    {/* Footer */}
                    <div style={{textAlign: 'center', marginTop: '15px', fontSize: '11px', color: '#aaa', borderTop: '1px solid #eee', paddingTop: '8px'}}>
                      تم إنشاء الكشف بتاريخ {format(new Date(), 'yyyy/MM/dd HH:mm')}
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="empty-state">
          <FileBarChart className="empty-state-icon" />
          <h3 className="empty-state-title">اختر الفترة الزمنية</h3>
          <p className="empty-state-description">حدد تاريخ البداية والنهاية ثم اضغط على "إنشاء التقرير"</p>
        </div>
      )}
    </div>
  );
};

export default ReportsPage;
